import { useEffect, useState, useMemo } from "react";
import { Link, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import UpcomingEvents from "@/components/UpcomingEvents";
import ParentAssignmentsBanner from "@/components/ParentAssignmentsBanner";
import { CATEGORY_META } from "@/components/CommentCategories";
import { GraduationCap, CalendarX, MessageSquare, ChevronRight, Sparkles, AlertTriangle, TrendingUp, CalendarClock } from "lucide-react";

const StatusStrip = ({ summary }) => {
    // Status (well-being): from comment categories average
    let wellLabel = "Pa të dhëna";
    let wellTone = "bg-slate-50 border-slate-200 text-slate-600";
    if (summary.commentScore != null) {
        if (summary.commentScore >= 1.2) { wellLabel = "Shumë mirë"; wellTone = "bg-sage/15 border-sage/30 text-sage"; }
        else if (summary.commentScore >= 0.3) { wellLabel = "Mirë"; wellTone = "bg-seablue/15 border-seablue/30 text-seablue"; }
        else if (summary.commentScore >= -0.3) { wellLabel = "Neutral"; wellTone = "bg-slate-100 border-slate-200 text-slate-700"; }
        else if (summary.commentScore >= -1.2) { wellLabel = "Keq"; wellTone = "bg-orange-100 border-orange-200 text-orange-800"; }
        else { wellLabel = "Shumë keq"; wellTone = "bg-terracotta/15 border-terracotta/30 text-terracotta"; }
    }

    // Progress (learning): from grades + homework completion
    let prgLabel = "Pa të dhëna ende";
    let prgTone = "bg-slate-50 border-slate-200 text-slate-600";
    if (summary.progressScore != null) {
        const p = summary.progressScore; // -2..+2
        if (p >= 1.2) { prgLabel = "Shumë mirë"; prgTone = "bg-sage/15 border-sage/30 text-sage"; }
        else if (p >= 0.3) { prgLabel = "Mirë"; prgTone = "bg-seablue/15 border-seablue/30 text-seablue"; }
        else if (p >= -0.3) { prgLabel = "Neutral"; prgTone = "bg-sand/20 border-sand/40 text-amber-800"; }
        else if (p >= -1.2) { prgLabel = "Keq"; prgTone = "bg-orange-100 border-orange-200 text-orange-800"; }
        else { prgLabel = "Shumë keq"; prgTone = "bg-terracotta/15 border-terracotta/30 text-terracotta"; }
    }

    // Action: serious if 3+ "1" grades or 2+ negative comments (bad/very_bad/warning)
    const actionNeeded = summary.lowGrades >= 3 || summary.negativeComments >= 2;
    const actLabel = actionNeeded ? "Kërkon vëmendje serioze" : "Gjithçka në rregull";
    const actTone = actionNeeded ? "bg-red-50 border-red-300 text-red-800" : "bg-sage/15 border-sage/30 text-sage";

    const card = (icon, label, value, tone, testId) => (
        <div className={`rounded-xl border p-4 flex items-start gap-3 ${tone}`} data-testid={testId}>
            <div className="w-9 h-9 rounded-lg bg-white/60 flex items-center justify-center shrink-0">{icon}</div>
            <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">{label}</div>
                <div className="font-heading font-bold text-base leading-tight mt-0.5 truncate">{value}</div>
            </div>
        </div>
    );

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-h-[200px]" data-testid="parent-status-strip">
            {card(<Sparkles className="w-5 h-5" />, "Status (mirëqenia)", wellLabel, wellTone, "status-card-wellbeing")}
            {card(<TrendingUp className="w-5 h-5" />, "Progresi (mësimi)", prgLabel, prgTone, "status-card-progress")}
            {card(<AlertTriangle className="w-5 h-5" />, "Veprimi", actLabel, actTone, "status-card-action")}
        </div>
    );
};

const NavCard = ({ to, icon, title, subtitle, value, tone = "oxford", testId }) => (
    <Link to={to} data-testid={testId}
        className="group bg-white rounded-2xl border border-slate-200 p-7 hover:border-oxford hover:shadow-lg transition-all flex flex-col">
        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 ${tone === "oxford" ? "bg-oxford text-white" : tone === "terracotta" ? "bg-terracotta text-white" : "bg-sage text-white"}`}>
            {icon}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500">{subtitle}</div>
        <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{title}</div>
        {value && <div className="mt-4 text-3xl font-heading font-black text-oxford">{value}</div>}
        <div className="mt-auto pt-4 flex items-center gap-1.5 text-sm text-oxford font-semibold">
            Shih më shumë <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition" />
        </div>
    </Link>
);

export default function ParentOverview() {
    const [children, setChildren] = useState([]);
    const [data, setData] = useState(null);
    const [absences, setAbsences] = useState({ total_hours: 0, total_count: 0 });
    const [comments, setComments] = useState({ comments: [], summary_score: null, total: 0 });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const cr = await api.get("/parent/children");
                setChildren(cr.data);
                if (cr.data.length > 0) {
                    const sid = cr.data[0].id;
                    const [r1, r2, r3] = await Promise.all([
                        api.get(`/students/${sid}/records`),
                        api.get(`/students/${sid}/absences`),
                        api.get(`/students/${sid}/comments`),
                    ]);
                    setData(r1.data);
                    setAbsences(r2.data);
                    setComments(r3.data);
                }
            } finally { setLoading(false); }
        })();
    }, []);

    const summary = useMemo(() => {
        if (!data) return { progressScore: null, commentScore: null, lowGrades: 0, negativeComments: 0, avgGrade: null };
        // Progress: combine cycle_grade averages + homework completion ratio
        const finals = [];
        let lowGrades = 0;
        let totalSlots = 0, doneSlots = 0;
        for (const r of data.records) {
            ["1", "2"].forEach((s) => ["1", "2"].forEach((c) => {
                const cyc = r.semesters[s][c];
                cyc.homework.forEach((h) => {
                    totalSlots++;
                    if (["delivered", "late", "not_good", "copy", "missed"].includes(h)) doneSlots++;
                });
            }));
            ["1", "2"].forEach((s) => {
                const f = r.computed[s].final;
                if (f != null) {
                    finals.push(f);
                    if (f <= 1.5) lowGrades++;
                }
            });
        }
        const avgGrade = finals.length ? finals.reduce((a, b) => a + b, 0) / finals.length : null;
        // Map avgGrade (1..5) to score range (-2..+2): 5->2, 4->1, 3->0, 2->-1, 1->-2
        let progressScore = null;
        if (avgGrade != null) progressScore = avgGrade - 3;

        const negativeComments = comments.comments.filter((c) => ["bad", "very_bad", "warning"].includes(c.category)).length;
        return {
            progressScore,
            avgGrade,
            commentScore: comments.summary_score,
            lowGrades,
            negativeComments,
        };
    }, [data, comments]);

    if (loading) return <Layout><div className="text-slate-500">Duke ngarkuar…</div></Layout>;

    if (children.length === 0) {
        return (
            <Layout>
                <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center" data-testid="parent-no-children">
                    <GraduationCap className="w-10 h-10 text-slate-400 mx-auto mb-4" />
                    <div className="font-heading text-xl font-bold">Asnjë fëmijë i lidhur</div>
                    <p className="text-sm text-slate-600 mt-2">Mësuesi do të lidhë fëmijën tuaj me llogarinë.</p>
                </div>
            </Layout>
        );
    }

    if (children.length > 1) return <Navigate to="/parent/children" replace />;

    const child = children[0];

    return (
        <Layout>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
                <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Përmbledhje</div>
                    <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 tracking-tight">{child.name}</h1>
                    <p className="text-slate-600 mt-2">Viti {child.year} • Shkolla e Mesme</p>
                </div>
                <Link to={`/parent/student/${child.id}/report`} data-testid="pdf-report-btn"
                    className="inline-flex items-center gap-2 bg-oxford text-white text-sm font-semibold rounded-lg px-4 py-2.5 hover:bg-oxfordhover transition self-start sm:self-auto">
                    Shkarko PDF
                </Link>
            </div>

            <ParentAssignmentsBanner />

            <div className="mb-8"><StatusStrip summary={summary} /></div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <NavCard to={`/parent/student/${child.id}`} icon={<GraduationCap className="w-7 h-7" />} subtitle="Notat" title="Notat sipas lëndës" value={summary.avgGrade ? summary.avgGrade.toFixed(1) : "—"} tone="oxford" testId="parent-nav-grades" />
                <NavCard to={`/parent/student/${child.id}/absences`} icon={<CalendarX className="w-7 h-7" />} subtitle="Mungesat" title="Mungesat e regjistruara" value={`${absences.total_hours} orë`} tone="terracotta" testId="parent-nav-absences" />
                <NavCard to={`/parent/student/${child.id}/comments`} icon={<MessageSquare className="w-7 h-7" />} subtitle="Komentet" title="Komentet e mësuesve" value={`${comments.total}`} tone="sage" testId="parent-nav-comments" />
            </div>

            {/* Consultations CTA */}
            <Link to="/parent/consultations" data-testid="parent-consult-cta"
                className="mt-6 group flex items-center gap-4 bg-gradient-to-r from-oxford to-oxfordhover text-white rounded-2xl p-5 hover:shadow-lg transition">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                    <CalendarClock className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-white/70 font-bold">Konsulta me mësuesit</div>
                    <div className="font-heading text-lg font-bold">Rezervo një takim 30-min</div>
                    <div className="text-xs text-white/80 mt-0.5">Cakto kohë me mësuesit ose me kujdestarin e klasës</div>
                </div>
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition" />
            </Link>

            <div className="mt-5"><UpcomingEvents days={14} limit={4} /></div>
        </Layout>
    );
}
